import React, { useEffect, useMemo, useState } from 'react';
import { fetchTopChartsCompaniesRanks, fetchTopChartsCompaniesTrends } from '../services';
import { getCountryFlag } from '@/pages/home/_config/countries';
import { SortType, SortMode, ChartType } from '@/pages/intelligence/pages/TopCharts/_config/enums';
import {
  TOP_COMPANIES_NUM,
  TOP_COMPANIES_DATASOURCES_MAPPING,
  TOP_COMPANIES_METRICS_MAPPING,
} from '@/pages/intelligence/pages/TopCharts/_config/topChartsConst';
import { IPO_STATUSES } from '@/pages/intelligence/components/TeaSelector/IPOStatusSelector';
import {
  TOP_CHARTS_TYPE_KEYS_MAPPING,
  DEFAULT_GAME_TYPES_MAPPING,
  TOP_CHARTS_TYPE_DEFAULT_SORT,
} from '../constants';
import type { TableSelectionType } from '@/pages/intelligence/components/TableComponents/components/TableCheckbox';
import type { RecordItemType } from '@/pages/intelligence/components/SearchRecent/type';
import type {
  ITopChartsTypeOfCompanies,
  ITopChartsCompaniesFetchParams,
  ITopChartsCompaniesRank,
  ITopChartsCompaniesTrend,
} from '../types';
import { handleTableDefaultSelectGames } from '@/pages/intelligence/utils/tableUtils';
import {
  updateShortLinkFilterId,
  updateTimeFilterValue,
  URL_FILTER_KEY,
} from '@intelligence/utils/urlPathCfg';

interface IUseTopChartsCompaniesCoreConfig {
  topChartsType: ITopChartsTypeOfCompanies;
  headQuatersOptions: any[];
  filterValue: any;
  setFilterVale: React.Dispatch<React.SetStateAction<any>>;
  // 是否有短链接
  hasUrlShortLink?: boolean;
}

interface IUseTopChartsCompaniesCoreResponse {
  fetchParams: ITopChartsCompaniesFetchParams;
  // 宽表总数据条数
  tableTotal: number;
  // 宽表数据
  tableData: ITopChartsCompaniesRank[];
  // echarts图表类型
  chartType: ChartType;
  // echarts图表类型 setter
  setChartType: React.Dispatch<React.SetStateAction<ChartType>>;
  // 柱状图、饼图 数据源
  barOrPieData: ITopChartsCompaniesRank[];
  setBarOrPieData: React.Dispatch<React.SetStateAction<ITopChartsCompaniesRank[]>>;
  // 折线图数据源
  lineData: ITopChartsCompaniesTrend[];
  // 首次调用接口后的回调
  getGameData: Function;
  // 折线图接口后的回调
  getLineChartData: (params: ITopChartsCompaniesFetchParams, idList: string[]) => Promise<void>;
  // 宽表默认勾选的前6数据
  tableSelection: TableSelectionType[];
  setTableSelection: React.Dispatch<React.SetStateAction<TableSelectionType[]>>;
  // 宽表默认勾选的前6数据
  defaultSelectionGames: TableSelectionType[];
  // 是否重新操作过复选器提交
  isNewComfirm: boolean;
  // 是否重新操作过复选器提交 setter
  setIsNewComfirm: React.Dispatch<React.SetStateAction<boolean>>;
  // 是否重新勾选过游戏
  isNewCheckGame: boolean;
  // 是否重新勾选过游戏 setter
  setIsNewCheckGame: React.Dispatch<React.SetStateAction<boolean>>;
  /** 首次展示下载弹窗提示 */
  firstDownLoadBubleShow: boolean;
  setFirstDownLoadBubleShow: React.Dispatch<React.SetStateAction<boolean>>;
  /** 是否勾选过游戏，取消和全选为没勾选过, 可用于继承和加载可视化Lading  */
  operatedGame: boolean;
  isNodata?: boolean;
  // echarts图表类型 setter
  setOperatedGame: React.Dispatch<React.SetStateAction<boolean>>;
  handleClearSearch: () => void;
  transTableDataForSearch: (payload: any) => RecordItemType[];
}

/**
 * 获取前top的数据
 * @param list 源数据
 * @param end 结束索引
 * @returns 前top的数据
 */
const handleTableToBar = (
  list: ITopChartsCompaniesRank[],
  end = TOP_COMPANIES_NUM,
): ITopChartsCompaniesRank[] => {
  if (!Array.isArray(list)) return [];
  return list.slice(0, end);
};

export default function useTopChartsCompaniesCoreModel(
  config: IUseTopChartsCompaniesCoreConfig,
): IUseTopChartsCompaniesCoreResponse {
  const { topChartsType, headQuatersOptions, filterValue, setFilterVale, hasUrlShortLink } =
    config || {};
  const topChartsTypeKey = TOP_CHARTS_TYPE_KEYS_MAPPING[topChartsType];
  const topChartsTypeSort = TOP_CHARTS_TYPE_DEFAULT_SORT[topChartsType];
  // 查询表单对象
  const [fetchParams, setFetchParams] = useState<ITopChartsCompaniesFetchParams>({
    ...filterValue,
    /* -----页面其他参数----- */
    topchart_name: topChartsTypeKey ?? 'revenue',
    data_sources: TOP_COMPANIES_DATASOURCES_MAPPING[topChartsType],
    metrics: TOP_COMPANIES_METRICS_MAPPING[topChartsType],
    /* -----页面其他参数----- */

    /* -----筛选器参数----- */
    game_type: DEFAULT_GAME_TYPES_MAPPING[topChartsType],
    regions: ['global'],
    genre: [],
    sub_genre: [],
    listed: IPO_STATUSES,
    company_headquarter: [],
    granularity: 'monthly',
    start_date: '',
    end_date: '',
    /* -----筛选器参数----- */

    /* -----排序、分页参数----- */
    order_source: topChartsTypeSort?.dataSource ?? '',
    order_metric: topChartsTypeSort?.key,
    order: SortType.Desc,
    ratio_type: SortMode.Value, // 环比排序是1 ，默认值排序是0
    page: 1,
    page_size: 100,
    /* -----排序、分页参数----- */

    /* -----搜索参数----- */
    search_name: '',
    company_ids: [],
    /* -----搜索参数----- */
  });

  // 宽表总数据条数
  const [tableTotal, setTableTotal] = useState(0);
  // 宽表数据
  const [tableData, setTableData] = useState<ITopChartsCompaniesRank[]>([]);
  // echarts图表类型
  const [chartType, setChartType] = useState<ChartType>(ChartType.Bar);
  // 柱状图、饼图 数据源
  const [barOrPieData, setBarOrPieData] = useState<ITopChartsCompaniesRank[]>([]);
  // 折线图数据源
  const [lineData, setLineData] = useState<ITopChartsCompaniesTrend[]>([]);
  // 宽表勾选的数据
  const [defaultSelectionGames, setDefaultSelectionGames] = useState<TableSelectionType[]>([]);
  const [tableSelection, setTableSelection] = useState<TableSelectionType[]>([]);

  // 是否重新操作过复选器提交
  const [isNewComfirm, setIsNewComfirm] = useState(true);
  // 是否重新勾选过游戏
  const [isNewCheckGame, setIsNewCheckGame] = useState(true);
  /** 真正的是否重新勾选游戏， 上面那个isNewCheckGame需要做优化处理 */
  const [operatedGame, setOperatedGame] = useState(false);
  const [firstDownLoadBubleShow, setFirstDownLoadBubleShow] = useState(false);
  const [tableListForClearSearch, setTableListForClearSearch] = useState<ITopChartsCompaniesRank[]>(
    [],
  );
  const [totalForClearSearch, setTotalForClearSearch] = useState(0);

  const isNodata = useMemo(
    () => tableTotal < 1 || tableSelection?.length < 1,
    [tableTotal, tableSelection],
  );

  const getGameData = async (argums: {
    params: ITopChartsCompaniesFetchParams;
    isCheckOrSearch: boolean;
    //  isUpdateLineChart: boolean;
    updateFilterValues: any;
    filterSaveName?: string;
  }) => {
    const { params, isCheckOrSearch, updateFilterValues = filterValue, filterSaveName } = argums;

    let paramsRes = { ...params };
    let tableSelectionRes = [...tableSelection];
    // 当时间为空时，等时间返回再更新，否则直接写token到接口
    const isUpdateShortLinkNow = !!params?.start_date && !!params?.end_date;
    // 时间如果是空的，则需要更新时间筛选器
    const needUpdateTime = !params?.start_date || !params?.end_date;
    if (isUpdateShortLinkNow && hasUrlShortLink) {
      updateShortLinkFilterId({ [URL_FILTER_KEY]: updateFilterValues });
    }
    try {
      setTableData([]);
      setFetchParams(params);
      const { total, list, req } = await fetchTopChartsCompaniesRanks(params, {
        headQuatersOptions,
      });
      if (params.page === 1) {
        /** 第一页时拿到默认前6个游戏作为游戏全选值 */
        const defaultGames = handleTableDefaultSelectGames(
          list.slice(0, TOP_COMPANIES_NUM),
          'app_id',
        );
        setDefaultSelectionGames(defaultGames);
        /** 在第一页且没有勾选游戏时默认选中前6（游戏在排序或重新筛选时有更新） */
        if (!isCheckOrSearch) {
          setTableSelection(defaultGames);
          /** 分页排序请求接口时，可视化不改变 */
          const barOrPieDataTmp = handleTableToBar(list, TOP_COMPANIES_NUM);
          setBarOrPieData(barOrPieDataTmp);
          tableSelectionRes = [...defaultGames];
        }
      }
      if (!params?.company_ids?.length) {
        // 有game_code表示是搜索，则不存页面数据
        setTableListForClearSearch(list || []);
        setTotalForClearSearch(total || list?.length);
      }
      const { start_date: startDate, end_date: endDate } = req || {};
      const fetchParamsTmp = {
        ...params,
        start_date: startDate || '',
        end_date: endDate || '',
      };
      paramsRes = { ...fetchParamsTmp };
      setFetchParams(fetchParamsTmp);
      setTableTotal(total || list?.length);
      setTableData(list || []);

      // 获取到时间后更新筛选器的值，并更新url短链接
      if (needUpdateTime && startDate && endDate) {
        const newFilterValue = updateTimeFilterValue({
          // hasUrlShortLinkVal,
          haveFilterSaveBtn: true,
          filterSaveName,
          filterValue: updateFilterValues,
          timeFilterValues: {
            type: fetchParamsTmp.granularity,
            value: [fetchParamsTmp.start_date, fetchParamsTmp.end_date],
          },
        });
        setFilterVale(newFilterValue);
        // 如果是时间为空等时间返回更新 且有短链接功能，则更新短链接
        if (!isUpdateShortLinkNow && hasUrlShortLink) {
          updateShortLinkFilterId({ [URL_FILTER_KEY]: newFilterValue });
        }
      }
    } catch (e) {
      console.error(e);
      setTableData([]);
      setBarOrPieData([]);
    } finally {
    }
    return { fetchParams: paramsRes, tableSelection: tableSelectionRes };
  };

  const getLineChartData = async (params: ITopChartsCompaniesFetchParams, idList: string[]) => {
    if (idList?.length > 0) {
      try {
        const fetchParamsTmp = {
          ...params,
          company_ids: idList,
        };
        setLineData([]);
        const { list /* barList */ } = await fetchTopChartsCompaniesTrends(
          { ...fetchParamsTmp },
          { headQuatersOptions },
        );
        setLineData(list);
        setIsNewComfirm(false);
        setIsNewCheckGame(false);
      } catch (e) {
        console.error(e);
      } finally {
      }
    } else {
      // 未勾选任何游戏，不请求，返回为空数据
      setLineData([]);
    }
  };

  const handleClearSearch = () => {
    setFetchParams({ ...fetchParams, company_ids: [] });
    setTableData(tableListForClearSearch);
    setTableTotal(totalForClearSearch);
  };

  const transTableDataForSearch = (res: any) => {
    const resultList = res?.list;
    if (!Array.isArray(resultList) || !resultList?.length) {
      return [];
    }
    const tranksList: RecordItemType[] = resultList.map((item) => {
      const countryCode = item?.location_identifiers_country_code;
      return {
        id: item?.id,
        imgSrc: item?.cover,
        lineOneStr: item?.name,
        lineTwoImg: getCountryFlag(countryCode),
        lineTwoImgName: countryCode,
        // imageType: '',
        lineTwoStr: item?.location_identifiers_country,
      };
    });

    return tranksList;
  };
  return {
    isNodata,
    fetchParams,
    tableTotal,
    tableData,
    chartType,
    setChartType,
    barOrPieData,
    lineData,
    getGameData,
    getLineChartData,
    defaultSelectionGames,
    isNewComfirm,
    setIsNewComfirm,
    isNewCheckGame,
    setIsNewCheckGame,
    tableSelection,
    setBarOrPieData,
    firstDownLoadBubleShow,
    setFirstDownLoadBubleShow,
    operatedGame,
    setOperatedGame,
    setTableSelection,
    handleClearSearch,
    transTableDataForSearch,
  };
}
